<template>
  <div>
    <div v-if="loading">
      <v-progress-linear indeterminate height="2px"></v-progress-linear>
      <div class="spinner caption">Loading...</div>
    </div>

    <v-slide-y-transition>
      <div v-show="!loading">
        <div class="spinner">
          <v-btn color="secondary" class="white--text" @click="downloadCSV()">
            Download List
            <v-icon right dark>download</v-icon>
          </v-btn>
        </div>
        <v-text-field
          dense
          v-model="userSearch"
          outline
          label="Search for a user"
        ></v-text-field>
        <v-layout row>
          <v-flex>
            <v-checkbox v-model="showConfirmed" label="Confirmed"></v-checkbox>
          </v-flex>
          <v-flex>
            <v-checkbox
              v-model="showUnConfirmed"
              label="Unconfirmed"
            ></v-checkbox>
          </v-flex>
        </v-layout>
        <v-list two-line subheader>
          <v-list-tile v-for="(user, i) in filteredUsers" :key="i" avatar>
            <v-list-tile-avatar>
              <v-icon
                :color="user.status === 'CONFIRMED' ? 'success' : 'error'"
                >{{
                  user.status === "CONFIRMED" ? "check_circle" : "cancel"
                }}</v-icon
              >
            </v-list-tile-avatar>

            <v-tooltip left>
              <template v-slot:activator="{ on }">
                <v-list-tile-content v-on="on">
                  <v-list-tile-title>{{ user.username }}</v-list-tile-title>
                  <v-list-tile-sub-title>{{
                    `email: ${user.email}`
                  }}</v-list-tile-sub-title>
                </v-list-tile-content>
              </template>
              <span>
                <div>
                  {{ `${user.active ? "Active user" : "Inactive user"}` }}
                </div>
                <div>{{ `created: ${user.created}` }}</div>
                <div>{{ `phone: ${mappedPhoneNumber(user.phone)}` }}</div>
                <div>{{ `Sign-up status: ${user.status}` }}</div>
              </span>
            </v-tooltip>

            <v-list-tile-action>
              <v-layout row>
                <v-flex pa-1>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        icon
                        ripple
                        small
                        @click="confirmUser(user)"
                        :disabled="user.status === 'CONFIRMED'"
                      >
                        <v-icon color="success lighten-1">check</v-icon>
                      </v-btn>
                    </template>
                    <span>Confirm User </span>
                  </v-tooltip>
                </v-flex>
                <v-flex pa-1>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        icon
                        ripple
                        small
                        @click="deleteUser(user)"
                      >
                        <v-icon color="red lighten-1">delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Delete User</span>
                  </v-tooltip>
                </v-flex></v-layout
              >
            </v-list-tile-action>
          </v-list-tile>
        </v-list>
      </div>
    </v-slide-y-transition>
  </div>
</template>

<script>
import { getUsers, deleteUser, confirmUser } from "@/api/users";

export default {
  name: "UserList",

  data() {
    return {
      loading: true,
      users: [],
      userSearch: null,
      showConfirmed: true,
      showUnConfirmed: true,
    };
  },

  computed: {
    filteredUsers() {
      return this.users
        .filter((user) => {
          return (
            !this.userSearch ||
            user.username.toLowerCase().includes(this.userSearch.toLowerCase())
          );
        })
        .filter((user) => {
          return (
            (user.status === "CONFIRMED" && this.showConfirmed) ||
            (user.status === "UNCONFIRMED" && this.showUnConfirmed)
          );
        });
    },
  },

  methods: {
    getUsers() {
      getUsers()
        .then((response) => {
          this.users = response;
        })
        .catch((err) => {
          this.$swal.fire({
            icon: "error",
            text: err,
          });
        })
        .finally(() => (this.loading = false));
    },
    confirmUser(user) {
      this.loading = true;
      confirmUser(user.username)
        .then(() => {
          user.status = "CONFIRMED";
          this.$swal.fire({
            icon: "success",
            text: `User ${user.username} confirmed`,
          });
        })
        .catch((err) => {
          this.$swal.fire({
            icon: "error",
            text: err,
          });
        })
        .finally(() => (this.loading = false));
    },

    deleteUser(user) {
      this.$swal.fire({
        title: "Confirm",
        text: `Are you sure you want to delete the user ${user.username} This action may not be reversed.`,
        type: "warning",
        showCancelButton: true,
        cancelButtonText: "No",
        confirmButtonText: "Yes",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then((response) => {
        if (response.value) {
          this.loading = true;
          deleteUser(user.username)
            .then(() => {
              this.$swal.fire({
                icon: "success",
                text: `User ${user.username} successfully deleted`,
              });
              this.getUsers();
            })
            .catch((err) => {
              this.$swal.fire({
                icon: "error",
                text: err,
              });
            })
            .finally((this.loading = false));
        }
      });
    },

    downloadCSV() {
      const headers = [
        "username",
        "email",
        "phone",
        "created",
        "status",
        "active",
      ];

      const rows = this.users.map((user) => {
        return headers.map((key) => {
          return user[key];
        });
      });

      let csvContent =
        "data:text/csv;charset=utf-8," +
        [headers, ...rows].map((e) => e.join(",")).join("\n");

      let now = new Date();
      const offset = now.getTimezoneOffset();
      now = new Date(now.getTime() - offset * 60 * 1000);

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute(
        "download",
        `riparian_portal_users_${now.toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);

      link.click();
    },

    mappedPhoneNumber(number) {
      return number.replace("+", "");
    },
  },

  beforeMount() {
    this.getUsers();
  },
};
</script>

<style scoped>
.spinner {
  width: 50%;
  margin: 0 auto;
}
</style>
